import {
  Button,
  TextInput,
  DatePicker,
  Drawer,
  LoadingLoop,
  Select,
} from '@medeloop/medeloop-components-library';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import { getStudyById, getStudyOrganizations, updateStudy, UpdateStudyDTO } from 'src/api/studies';
import { selectAuthToken } from 'src/store/selectors';
import { showToast } from 'src/store/toast';
import { Study } from 'src/types/studies';

dayjs.extend(customParseFormat);

type EditStudyProps = {
  study: Study;
  onClose: () => void;
  onSave: () => void;
};

export const EditStudy = ({ study, onClose, onSave }: EditStudyProps) => {
  const token = useSelector(selectAuthToken);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const studyId = study.studyId;

  // set initial state
  const [studyName, setStudyName] = useState('');
  const [studyDisplayName, setStudyDisplayName] = useState('');
  const [studyDescription, setStudyDescription] = useState('');
  const [studyExpectedParticipants, setStudyExpectedParticipants] = useState(0);
  const [studyExpectedDurationNumber, setStudyExpectedDurationNumber] = useState(0);
  const [studyExpectedDurationUnit, setStudyExpectedDurationUnit] = useState('days');
  const [studyStartDate, setStudyStartDate] = useState<Date | undefined>(undefined);
  const [studyCompletionDate, setStudyCompletionDate] = useState<Date | undefined>(undefined);

  if (!studyId) {
    onClose();
  }

  const studyQuery = useQuery({
    queryKey: ['study', studyId],
    queryFn: async () => {
      if (studyId) {
        const [study, organizations] = await Promise.all([
          getStudyById({ token, id: studyId }),
          getStudyOrganizations({ token, id: studyId }),
        ]);

        setStudyName(study.study.name);
        setStudyDisplayName(study.study.displayName);
        setStudyDescription(study.study.description);
        setStudyExpectedParticipants(study.study.expectedParticipants);

        const durations = study.study.expectedParticipantDuration.split(' ');
        setStudyExpectedDurationNumber(parseInt(durations[0]) || 0);
        setStudyExpectedDurationUnit(durations[1] || 'days');

        // set default date if not available
        if (study.study.startDate === undefined) {
          study.study.startDate = dayjs().format('YYYY-MM-DD');
        }
        if (study.study.completionDate === undefined) {
          study.study.completionDate = dayjs().format('YYYY-MM-DD');
        }

        setStudyStartDate(dayjs(study.study.startDate, 'YYYY-MM-DD').toDate());
        setStudyCompletionDate(dayjs(study.study.completionDate, 'YYYY-MM-DD').toDate());

        return { study: study.study, organizations: organizations.organizations };
      }
    },
  });

  const saveStudyMutation = useMutation({
    mutationFn: (updatedStudy: UpdateStudyDTO) => {
      return updateStudy({ token, updatedStudy });
    },
    onSuccess: () => {
      dispatch(
        showToast({
          message: t('study_updated_successfully', { name: studyQuery.data?.study.displayName }),
          type: 'success',
        }),
      );
      studyQuery.refetch();
    },
  });

  const handleStartDate = (val: Date | undefined) => {
    if (val) {
      if (studyCompletionDate && val > studyCompletionDate) {
        setStudyStartDate(val);
        setStudyCompletionDate(val);
      } else {
        setStudyStartDate(val);
      }
    } else {
      setStudyStartDate(val);
    }
  };

  const handleEndDate = (val: Date | undefined) => {
    if (val) {
      if (studyStartDate && val < studyStartDate) {
        setStudyStartDate(val);
        setStudyCompletionDate(val);
      } else {
        setStudyCompletionDate(val);
      }
    } else {
      setStudyCompletionDate(val);
    }
  };

  const handleSave = () => {
    if (studyId) {
      saveStudyMutation.mutate({
        studyId,
        name: studyName,
        displayName: studyDisplayName,
        description: studyDescription,
        expectedParticipants: studyExpectedParticipants,
        expectedParticipantDuration: studyExpectedDurationNumber + ' ' + studyExpectedDurationUnit,
        startDate: dayjs(studyStartDate).format('YYYY-MM-DD'),
        completionDate: dayjs(studyCompletionDate).format('YYYY-MM-DD'),
      });
      onSave();
    }
  };

  return (
    <Fragment>
      {studyQuery.isLoading && (
        <div className="flex flex-grow items-center justify-center">
          <LoadingLoop className="w-16" />
        </div>
      )}
      {studyQuery.data && (
        <div className="flex flex-col flex-grow gap-4">
          {/* Header */}
          <Drawer.Header className="!mb-5">
            <div className="flex">
              <div className="flex gap-4 flex-col">
                <Drawer.Title>{t('edit_study')}</Drawer.Title>
              </div>

              {/* Actions */}
              <div className="flex flex-grow justify-end gap-4">
                <Button variant="secondary" color="white" onClick={onClose}>
                  {t('cancel')}
                </Button>
                <Button variant="primary" onClick={handleSave}>
                  {t('save_changes')}
                </Button>
              </div>
            </div>
          </Drawer.Header>
          {/* Title */}

          {/* Study Details */}
          <div className="flex flex-col gap-4">
            <span className="text-white font-bold text-lg">Study details</span>
            <div className="flex flex-row gap-4">
              <TextInput
                label={t('display_name_header')}
                value={studyDisplayName}
                onChange={(e) => setStudyDisplayName(e.target.value)}
              />
              <TextInput
                label={t('study_name')}
                value={studyName}
                onChange={(e) => setStudyName(e.target.value)}
              />
            </div>
            <TextInput
              label={t('description')}
              value={studyDescription}
              onChange={(e) => setStudyDescription(e.target.value)}
            />
          </div>

          {/* General Information */}
          <div className="flex flex-col gap-4">
            <span className="text-white font-bold text-lg">{t('general_information')}</span>
            <div className="flex flex-row gap-4 items-end">
              <TextInput
                label={t('expected_total_participants')}
                value={studyExpectedParticipants}
                type="number"
                onChange={(e) => setStudyExpectedParticipants(parseInt(e.target.value))}
              />
              <div className="flex flex-col pt-2 text-white w-full">
                <span>{t('expected_participant_duration_in_study')}</span>
                <div className="flex gap-2">
                  <TextInput
                    type="number"
                    placeHolder={t('example_num')}
                    max={100}
                    min={1}
                    value={studyExpectedDurationNumber}
                    onChange={(e) => setStudyExpectedDurationNumber(parseInt(e.target.value))}
                  />
                  <Select
                    options={[
                      { value: 'days', label: t('days') },
                      { value: 'weeks', label: t('weeks') },
                    ]}
                    value={studyExpectedDurationUnit}
                    onValueChange={(val) => setStudyExpectedDurationUnit(val)}
                    className="!w-[151px] mt-2"
                  />
                </div>
              </div>
              <TextInput
                label={t('participants_header')}
                value={studyQuery.data.study.numParticipants}
                disabled
              />
            </div>
            <div className="flex flex-row gap-4">
              <DatePicker
                hasIcon
                label={t('start_date')}
                value={studyStartDate}
                onSelect={handleStartDate}
              />
              <DatePicker
                hasIcon
                label={t('completion_date')}
                value={studyCompletionDate}
                onSelect={handleEndDate}
              />
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
